import { Injectable } from '@angular/core';
import {AuthService as Auth0Service, User} from "@auth0/auth0-angular";
import {lastValueFrom, timeout, catchError, BehaviorSubject, Observable} from "rxjs";
import { UtentiService } from './utenti.service';
import { Utente } from '../models/utente.model';
import { AuthService } from './auth.service';
import { NGXLogger } from 'ngx-logger';
import { EntiEsecutoriService } from './entiEsecutori.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public isAdmin:boolean        = false;
  public isEnteEsecutore:boolean        = false;
  public isUMVS:boolean        = false;
  private ruoloStr: string = '';
  private userData: Utente = new Utente();

  // BehaviorSubject per lo stato di login
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  // BehaviorSubject per le informazioni dell'utente
  private userInfoSubject = new BehaviorSubject<{ruolo: string, nome: string}>({ruolo: '', nome: ''});
  public userInfo$ = this.userInfoSubject.asObservable();

  constructor(
    private auth0: Auth0Service,
    private auth: AuthService,
    private utentiService: UtentiService,
    private entiService: EntiEsecutoriService,
    private logger: NGXLogger) {
    // Controlla se l'utente è già loggato
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    this.isLoggedInSubject.next(isLoggedIn);

    // Se c'è già un utente nel localStorage, aggiorna il subject
    if (isLoggedIn) {
      const ruolo = localStorage.getItem("ruolo") || '';
      const nome = localStorage.getItem("utente") || '';
      this.userInfoSubject.next({ ruolo, nome });
    }
  }

  async loadAndSetUserInformation (user: User) {
    if (!user.sub) {
      console.error('User sub is undefined');
      return;
    }

    //this.logger.info("[USER SERVICE] Inizio caricamento informazioni utente");

    // Verifica se il token è già disponibile
    const existingToken = this.auth.getToken();
    if (existingToken) {
     // this.logger.info("[USER SERVICE] Token già disponibile, procedo con caricamento dati");
      await this.loadUserDataWithToken(user, existingToken);
      return;
    }

    // Altrimenti ottieni il token
    //this.logger.info("[USER SERVICE] Richiedo token di autenticazione...");
    this.auth.getAccessTokenSilently().subscribe({
      next: async (token) => {
     //   this.logger.info("[USER SERVICE] Token ottenuto: " + (token ? 'sì' : 'no'));
        if (token) {
          await this.loadUserDataWithToken(user, token);
        } else {
          this.logger.error("[USER SERVICE] Token non disponibile");
        }
      },
      error: (error) => {
        this.logger.error("[USER SERVICE] Errore nell'ottenere il token:", error);
        console.error('Error getting token:', error);
      }
    });
  }

  private async loadUserDataWithToken(user: User, token: string) {
    try {
      // Verifica che user.sub esista
      if (!user.sub) {
        this.logger.error("[USER SERVICE] User.sub non definito");
        return;
      }

      let idAuth0:string = user.sub.slice(user.sub.indexOf('|')+1);
      //this.logger.info("[USER SERVICE] Caricamento dati utente con ID Auth0: " + idAuth0);

      const utente = await lastValueFrom(this.utentiService.getByIdAuth0(idAuth0));
      this.setUser(utente);


      //this.logger.info("[USER SERVICE] User roles:", user["https://pmo2.map-hosting.it/roles"]);

      this.ruoloStr = user["https://pmo2.map-hosting.it/roles"][0];
      this.setRoleFromAuth0(this.ruoloStr);
    //this.logger.info("[USER SERVICE] Ruolo:", this.ruoloStr);

      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("ruolo", this.ruoloStr);
      localStorage.setItem("idUtente", idAuth0);
      localStorage.setItem("utente", user.name || '');
      localStorage.setItem("enteId", utente.enteEsecutore?.id?.toString() || '');
      localStorage.setItem("enteLabel", utente.enteEsecutore?.label || '');

      if(this.ruoloStr==='ENTE_ESECUTORE'){
        this.entiService.getById(utente.enteEsecutore?.id).subscribe((ente) => {
          this.logger.info('[USER SERVICE] Ente esecutore:', ente);
          localStorage.setItem("latitudineComune", ente.comuni[0].latitudine?.toString() || '');
          localStorage.setItem("longitudineComune", ente.comuni[0].longitudine?.toString() || '');
        });
      }

      // Aggiorna i BehaviorSubject
      this.isLoggedInSubject.next(true);
      this.userInfoSubject.next({
        ruolo: this.ruoloStr,
        nome: user.name || ''
      });
    } catch (error) {
      this.logger.error("[USER SERVICE] Errore durante il caricamento delle informazioni utente:", error);
      console.error('Error loading user information:', error);
    }
  }

  public setRoleFromAuth0(role:string){
    if(role==='ADMIN' || role==='SUPERADMIN'){
        this.isAdmin = true;
    }else if(role==='UMVS'){
        this.isUMVS = true;
    }else if(role==='ENTE_ESECUTORE'){
        this.isEnteEsecutore = true;
    }
  }

  getUser(): Utente {
    return this.userData;
  }

  setUser(userData: Utente): void {
    this.userData = userData;
  }

  // Metodo per logout che aggiorna anche i subject
  public clearUserData(): void {
    this.isLoggedInSubject.next(false);
    this.userInfoSubject.next({ ruolo: '', nome: '' });
  }
}
